// src/pages/DebtStudio/components/Portfolios/Main/portfolioComposition.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import {
  ChevronRight,
  ChevronLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

const ISSUE_SELECT = `
  *,
  issuers:issuer_id (
    legal_name
  )
`;

const MEMBER_SELECT = `
  id,
  ds_deal_id,
  include,
  sort_order,
  created_at,
  ds_deals:ds_deal_id (
    ${ISSUE_SELECT}
  )
`;

function formatIssueLabel(dealRow) {
  if (!dealRow) return "(Unknown)";
  const issuer = dealRow?.issuers?.legal_name || dealRow?.issuer_name_raw || "";
  const name = dealRow?.name || dealRow?.deal_name || "(Untitled Issue)";
  return issuer ? `${issuer}, ${name}` : name;
}

function formatStatusOnly(rawStatus) {
  const s = typeof rawStatus === "string" ? rawStatus.trim() : "";
  if (!s) return "";
  const norm = s.toLowerCase();
  if (norm === "final") return "Final";
  if (norm === "prelim" || norm === "preliminary") return "Prelim";
  if (norm === "draft") return "Draft";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

async function fetchOrgLibraryIssues({ orgId, limit = 500 }) {
  if (!orgId) return [];

  const { data, error } = await supabase
    .from("ds_deal_org_access")
    .select(
      `
      deal_id,
      ds_deals:deal_id (
        ${ISSUE_SELECT}
      )
    `
    )
    .eq("org_id", orgId)
    .limit(limit);

  if (error) throw error;

  return (data ?? []).map((r) => r?.ds_deals).filter(Boolean);
}

/* ------------------------------------------------------------------
 * List Pane
 * ------------------------------------------------------------------ */

function ListPane({
  title,
  subtitle,
  items,
  selectedIds,
  onToggle,
  onToggleAll,
  emptyText,
  disabled,

  sortField,
  sortAsc,
  setSortField,
  setSortAsc,

  // Optional: search box aligned to the two-line title group
  showSearch = false,
  searchValue = "",
  onSearchChange = () => {},
  searchPlaceholder = "Search…",
}) {
  const allIds = useMemo(() => items.map((x) => x.id), [items]);

  const allSelected = useMemo(() => {
    if (!allIds.length) return false;
    return allIds.every((id) => selectedIds.has(id));
  }, [allIds, selectedIds]);

  const someSelected = useMemo(() => {
    if (!allIds.length) return false;
    return allIds.some((id) => selectedIds.has(id));
  }, [allIds, selectedIds]);

  const renderSortIcon = (active) => {
    if (!active) return <ArrowUpDown className="w-3.5 h-3.5" />;
    return sortAsc ? (
      <ArrowUp className="w-3.5 h-3.5 text-app" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-app" />
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc((v) => !v);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  return (
    <div className="min-w-0">
      <div className="rounded-xl border border-border bg-app overflow-hidden min-w-0">
        {/* Title header (fixed height, aligned across panes) */}
        <div className="px-3 py-2 border-b border-border">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-center min-h-[52px]">
            {/* Left: two-line group */}
            <div className="min-w-0">
              <div className="text-app font-semibold leading-5">{title}</div>
              {subtitle ? (
                <div className="text-muted text-sm leading-5">{subtitle}</div>
              ) : (
                // preserve height if no subtitle
                <div className="text-muted text-sm leading-5 opacity-0">.</div>
              )}
            </div>

            {/* Right: search aligned to the two-line group */}
            {showSearch ? (
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 w-56 max-w-full rounded-md border border-border bg-app px-2 text-sm text-app placeholder:text-muted"
              />
            ) : (
              // optional placeholder to keep symmetry
              <div className="w-56" />
            )}
          </div>
        </div>

        {/* Column header row */}
        <div className="grid grid-cols-[auto_1fr_96px] gap-2 px-3 py-2 border-b border-border text-sm font-semibold select-none">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && someSelected;
            }}
            onChange={() => onToggleAll?.(!allSelected)}
            disabled={disabled || items.length === 0}
            aria-label="Select all"
          />

          <div
            className="flex items-center gap-1 cursor-pointer"
            onClick={() => handleSort("label")}
          >
            Issue Name
            {renderSortIcon(sortField === "label")}
          </div>

          <div
            className="flex items-center justify-end gap-1 cursor-pointer"
            onClick={() => handleSort("status")}
          >
            Status
            {renderSortIcon(sortField === "status")}
          </div>
        </div>

        {/* Rows */}
        <div className="max-h-[420px] overflow-auto">
          {items.length === 0 ? (
            <div className="p-3 text-muted italic">{emptyText}</div>
          ) : (
            items.map((x) => {
              const checked = selectedIds.has(x.id);
              return (
                <label
                  key={x.id}
                  className="grid grid-cols-[auto_1fr_96px] gap-2 px-3 py-2 cursor-pointer hover:bg-white/10 min-w-0"
                >
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={checked}
                    onChange={() => onToggle?.(x.id)}
                    disabled={disabled}
                  />

                  <div
                    className="text-app text-xs whitespace-normal break-words min-w-0"
                    title={x.label}
                  >
                    {x.label}
                  </div>

                  <div className="text-muted text-sm text-right whitespace-nowrap">
                    {x.status || ""}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Main Component
 * ------------------------------------------------------------------ */

export default function PortfolioComposition({
  portfolio,
  canEdit = false,
  markDirty = () => {},
}) {
  const { profile, selectedOrg } = useProfile();

  const portfolioId = portfolio?.id || null;
  const orgId = selectedOrg?.id || null;
  const tenantId = selectedOrg?.tenant_id || profile?.tenant_id || null;

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [allIssues, setAllIssues] = useState([]);
  const [members, setMembers] = useState([]);

  const [selectedAvailableIds, setSelectedAvailableIds] = useState(new Set());
  const [selectedMemberDealIds, setSelectedMemberDealIds] = useState(new Set());

  // Search (Available Issues only)
  const [availableSearch, setAvailableSearch] = useState("");

  // Sort state
  const [availSortField, setAvailSortField] = useState("label");
  const [availSortAsc, setAvailSortAsc] = useState(true);

  const [memberSortField, setMemberSortField] = useState("label");
  const [memberSortAsc, setMemberSortAsc] = useState(true);

  const memberDealIdSet = useMemo(
    () => new Set(members.map((m) => m.ds_deal_id)),
    [members]
  );

  const availableIssueItems = useMemo(() => {
    const q = availableSearch.trim().toLowerCase();

    const rows = allIssues
      .filter((d) => d?.id && !memberDealIdSet.has(d.id))
      .map((d) => ({
        id: d.id,
        label: formatIssueLabel(d),
        status: formatStatusOnly(d?.status),
      }))
      .filter((x) => {
        if (!q) return true;
        return (x.label || "").toLowerCase().includes(q);
      });

    return [...rows].sort((a, b) => {
      const aVal = (a[availSortField] || "").toLowerCase();
      const bVal = (b[availSortField] || "").toLowerCase();
      if (aVal < bVal) return availSortAsc ? -1 : 1;
      if (aVal > bVal) return availSortAsc ? 1 : -1;
      return 0;
    });
  }, [
    allIssues,
    memberDealIdSet,
    availSortField,
    availSortAsc,
    availableSearch,
  ]);

  const memberIssueItems = useMemo(() => {
    const rows = members.map((m) => {
      const deal = m.ds_deals || null;
      return {
        id: m.ds_deal_id,
        label: formatIssueLabel(deal),
        status: formatStatusOnly(deal?.status),
      };
    });

    return [...rows].sort((a, b) => {
      const aVal = (a[memberSortField] || "").toLowerCase();
      const bVal = (b[memberSortField] || "").toLowerCase();
      if (aVal < bVal) return memberSortAsc ? -1 : 1;
      if (aVal > bVal) return memberSortAsc ? 1 : -1;
      return 0;
    });
  }, [members, memberSortField, memberSortAsc]);

  /* ------------------------------------------------------------------
   * Selection logic (mutually exclusive panes)
   * ------------------------------------------------------------------ */

  const toggleAvailable = useCallback((dealId) => {
    setSelectedAvailableIds((prev) => {
      const next = new Set(prev);
      next.has(dealId) ? next.delete(dealId) : next.add(dealId);
      return next;
    });
    setSelectedMemberDealIds(new Set());
  }, []);

  const toggleMembers = useCallback((dealId) => {
    setSelectedMemberDealIds((prev) => {
      const next = new Set(prev);
      next.has(dealId) ? next.delete(dealId) : next.add(dealId);
      return next;
    });
    setSelectedAvailableIds(new Set());
  }, []);

  const toggleAllAvailable = useCallback(
    (select) => {
      setSelectedAvailableIds(
        select ? new Set(availableIssueItems.map((x) => x.id)) : new Set()
      );
      setSelectedMemberDealIds(new Set());
    },
    [availableIssueItems]
  );

  const toggleAllMembers = useCallback(
    (select) => {
      setSelectedMemberDealIds(
        select ? new Set(memberIssueItems.map((x) => x.id)) : new Set()
      );
      setSelectedAvailableIds(new Set());
    },
    [memberIssueItems]
  );

  /* ------------------------------------------------------------------
   * Data fetch
   * ------------------------------------------------------------------ */

  const fetchAll = useCallback(async () => {
    if (!orgId || !portfolioId) return;

    setLoading(true);
    setError("");

    try {
      const [{ data: issuesData, error: issuesErr }, libIssues, membersRes] =
        await Promise.all([
          supabase
            .from("ds_deals")
            .select(ISSUE_SELECT)
            .eq("org_id", orgId)
            .order("updated_at", { ascending: false })
            .limit(500),
          fetchOrgLibraryIssues({ orgId, limit: 500 }),
          supabase
            .from("ds_portfolio_issues")
            .select(MEMBER_SELECT)
            .eq("org_id", orgId)
            .eq("portfolio_id", portfolioId)
            .order("sort_order", { ascending: true, nullsFirst: false })
            .order("created_at", { ascending: true }),
        ]);

      if (issuesErr) throw issuesErr;
      if (membersRes.error) throw membersRes.error;

      const ws = Array.isArray(issuesData) ? issuesData : [];
      const lib = Array.isArray(libIssues) ? libIssues : [];

      // Merge + dedupe by deal id
      const merged = [];
      const seen = new Set();

      for (const d of ws) {
        if (!d?.id) continue;
        if (seen.has(d.id)) continue;
        seen.add(d.id);
        merged.push(d);
      }
      for (const d of lib) {
        if (!d?.id) continue;
        if (seen.has(d.id)) continue;
        seen.add(d.id);
        merged.push(d);
      }

      setAllIssues(merged);
      setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);

      setSelectedAvailableIds(new Set());
      setSelectedMemberDealIds(new Set());
    } catch (e) {
      console.error("❌ [PortfolioComposition] fetch failed:", e);
      setError(e?.message || "Failed to load issues.");
      setAllIssues([]);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, portfolioId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ------------------------------------------------------------------
   * Actions
   * ------------------------------------------------------------------ */

  const handleAdd = useCallback(async () => {
    if (!canEdit || !orgId || !tenantId || !portfolioId) return;
    if (selectedAvailableIds.size === 0) return;

    setBusy(true);
    setError("");

    try {
      const payload = Array.from(selectedAvailableIds).map((dealId) => ({
        tenant_id: tenantId,
        org_id: orgId,
        portfolio_id: portfolioId,
        ds_deal_id: dealId,
        include: true,
        sort_order: null,
      }));

      const { error: insErr } = await supabase
        .from("ds_portfolio_issues")
        .insert(payload);

      if (insErr) throw insErr;

      markDirty?.();
      await fetchAll();
    } catch (e) {
      console.error("❌ [PortfolioComposition] add failed:", e);
      setError(e?.message || "Failed to add issues to the portfolio.");
    } finally {
      setBusy(false);
    }
  }, [
    canEdit,
    orgId,
    tenantId,
    portfolioId,
    selectedAvailableIds,
    fetchAll,
    markDirty,
  ]);

  const handleRemove = useCallback(async () => {
    if (!canEdit || !orgId || !portfolioId) return;
    if (selectedMemberDealIds.size === 0) return;

    setBusy(true);
    setError("");

    try {
      const { error: delErr } = await supabase
        .from("ds_portfolio_issues")
        .delete()
        .eq("org_id", orgId)
        .eq("portfolio_id", portfolioId)
        .in("ds_deal_id", Array.from(selectedMemberDealIds));

      if (delErr) throw delErr;

      markDirty?.();
      await fetchAll();
    } catch (e) {
      console.error("❌ [PortfolioComposition] remove failed:", e);
      setError(e?.message || "Failed to remove issues from the portfolio.");
    } finally {
      setBusy(false);
    }
  }, [canEdit, orgId, portfolioId, selectedMemberDealIds, fetchAll, markDirty]);

  const canAdd = canEdit && selectedAvailableIds.size > 0 && !busy && !loading;
  const canRemove =
    canEdit && selectedMemberDealIds.size > 0 && !busy && !loading;

  return (
    <div className="space-y-4">
      <SectionCard title="Composition">
        {error && (
          <div className="mt-3 rounded-md border border-border bg-app p-3 text-sm">
            <div className="text-red-600 font-medium">Issue</div>
            <div className="text-muted mt-1">{error}</div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-4 items-start text-sm">
          <ListPane
            title="Available Issues"
            subtitle={loading ? "Loading…" : `${availableIssueItems.length} available`}
            items={availableIssueItems}
            selectedIds={selectedAvailableIds}
            onToggle={toggleAvailable}
            onToggleAll={toggleAllAvailable}
            emptyText="No available issues (all issues are already in the portfolio)."
            disabled={loading || busy}
            sortField={availSortField}
            sortAsc={availSortAsc}
            setSortField={setAvailSortField}
            setSortAsc={setAvailSortAsc}
            showSearch
            searchValue={availableSearch}
            onSearchChange={setAvailableSearch}
            searchPlaceholder="Search issues…"
          />

          {/* Action arrows */}
          <div className="flex md:flex-col gap-3 justify-start items-center pt-28">
            <button
              type="button"
              className="btn btn-primary px-2"
              onClick={handleAdd}
              disabled={!canAdd}
              aria-label="Add selected issues"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              type="button"
              className="btn px-2 bg-amber-600/80 hover:bg-amber-600 text-white disabled:opacity-50"
              onClick={handleRemove}
              disabled={!canRemove}
              aria-label="Remove selected issues"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>

          <ListPane
            title="Portfolio Members"
            subtitle={loading ? "Loading…" : `${memberIssueItems.length} members`}
            items={memberIssueItems}
            selectedIds={selectedMemberDealIds}
            onToggle={toggleMembers}
            onToggleAll={toggleAllMembers}
            emptyText="No issues in this portfolio yet."
            disabled={loading || busy}
            sortField={memberSortField}
            sortAsc={memberSortAsc}
            setSortField={setMemberSortField}
            setSortAsc={setMemberSortAsc}
          />
        </div>
      </SectionCard>
    </div>
  );
}
